import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarHeader from "./SidebarHeader";
import NavigationItem from "./NavigationItem";
import UserAvatar from "../UserAvatar/UserAvatar";
import useNavigation from "../../../hooks/useNavigation";
import "./SidebarNavigation.css";

function SidebarNavigation({
  isCollapsed = false,
  isMobileOpen = false,
  onToggle,
}) {
  const navigate = useNavigate();
  const { navigationItems, isNavigationItemActive, currentPath, debug } =
    useNavigation();
  const unreadMessageCount = 0;

  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggle = () => {
    // Only allow toggle on desktop (not mobile since bottom nav is always visible)
    if (!isMobile && onToggle) {
      onToggle();
    }
  };

  // Handle navigation item clicks with proper active state management
  const handleNavigationClick = navItem => {
    // Navigate to the new route
    navigate(navItem.path);

    // Debug logging in development
    if (process.env.NODE_ENV === "development" && debug) {
      // Navigation clicked: ${navItem.label} to path: ${navItem.path}
      // Log active state after a brief delay to allow route change
      setTimeout(() => debug.logActiveState(), 100);
    }
  };

  // Debug effect to log navigation state changes
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && debug) {
      // SidebarNavigation rendered with path: ${currentPath}
    }
  }, [currentPath, debug]);

  // Determine sidebar classes based on state
  const getSidebarClasses = () => {
    const classes = ["sidebar-navigation"];

    if (isMobile) {
      classes.push("mobile");
      classes.push(isMobileOpen ? "mobile-open" : "mobile-closed");
    } else {
      classes.push(isCollapsed ? "collapsed" : "expanded");
    }

    return classes.join(" ");
  };

  return (
    <nav
      className={getSidebarClasses()}
      role="navigation"
      aria-label="Main navigation"
      aria-hidden={false}
    >
      {/* Sidebar Header */}
      <SidebarHeader isCollapsed={isCollapsed} onToggle={handleToggle} />

      {/* Sidebar Menu */}
      <div className="sidebar-menu">
        {navigationItems.map(item => (
          <NavigationItem
            key={item.id}
            item={{
              ...item,
              badgeCount: item.id === "chat" ? unreadMessageCount : 0,
            }}
            isActive={isNavigationItemActive(item)}
            isCollapsed={isCollapsed}
            onClick={handleNavigationClick}
          />
        ))}
      </div>

      {/* Sidebar Footer with User Avatar */}
      <div className="sidebar-footer">
        <UserAvatar size="medium" showName={!isCollapsed && !isMobile} />
      </div>
    </nav>
  );
}

export default SidebarNavigation;
